import {createRule, type Maybe} from '@regle/core';
import {maxValue} from '@regle/rules';
import {useFormat, useTranslation} from '#imports';
import type {Currency} from '../../common';

/**
 * Requires a field to have a specified maximum price value.
 *
 * @param maxPrice - the maximum price
 * @param currency - the currency to display in the error message
 *
 *
 * ```ts
 *  import { useRegle, maxPrice } from '@malt/forms';
 *
 *  const user = ref<User>();
 *
 *  const { r$ } = useRegle(... , {
 *    amount: { maxPrice: maxPrice(1000, user.currency)}
 *  })
 * ```
 */
export const maxPrice = createRule({
    type: 'maxValue',
    validator: (value: Maybe<number>, maxPrice: number, currency: Currency = 'EUR', options?: {allowEqual: boolean}) => {
        return maxValue(maxPrice, options).exec(value);
    },
    message: ({$params: [maxPrice, currency, options]}) => {
        const {t} = useTranslation();
        const {formatAmount} = useFormat();

        return (options?.allowEqual ?? true)
            ? t('generic.forms.validation.errors.maxValue.equal', [formatAmount(currency, maxPrice)])
            : t('generic.forms.validation.errors.maxValue', [formatAmount(currency, maxPrice)]);
    },
});
